import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupSubmenuListeners();
    this.#setupNavigationLinkListeners();
    this.#initializeButtons();
    this.#setupSearchModalListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    const clickedSummary = event.target.closest('summary');
    
    if (clickedSummary) {
      const parentDetails = clickedSummary.parentElement;
      
      if (parentDetails instanceof HTMLDetailsElement && 
          parentDetails.classList.contains('menu-drawer__menu-container')) {
        return parentDetails;
      }
    }

    const submenuContainer = event.target.closest('details.menu-drawer__menu-container');
    
    if (submenuContainer instanceof HTMLDetailsElement) {
      return submenuContainer;
    }
    return this.refs.details;
  }


  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {

    if (!event || !(event.target instanceof Element)) {
      const details = this.refs.details;
      const summary = details.querySelector('summary');
      if (!summary) return;

      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
      this.#toggleAnnouncementBar(true);
      this.#showButton('close');

      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
      return;
    } else {
      const details = this.#getDetailsElement(event); 
      const summary = details.querySelector('summary');

      if (!summary) return;

      summary.setAttribute('aria-expanded', 'true');

      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
    }

    const details = this.#getDetailsElement(event);
    const isMainDrawer = details === this.refs.details;

    if (isMainDrawer) {
      const summary = details.querySelector('summary');
      if (!summary) return;

      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
      this.#toggleAnnouncementBar(true);
      this.#showButton('close');

      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
    }
  }

  /**
   * Go back or close the main menu drawer
   */
  back() {
    const openSubmenu = this.querySelector('details.menu-drawer__menu-container[open]');
    
    if (openSubmenu instanceof HTMLDetailsElement) {
      const summary = openSubmenu.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
      }
      openSubmenu.classList.remove('menu-open');
      openSubmenu.removeAttribute('open');
      
      this.#showButton('close');
      
      setTimeout(() => {
        trapFocus(this.refs.details);
      }, 0);
    }
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) {
      return;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      details.setAttribute('data-animating', '');
      this.#toggleAnnouncementBar(false);
      this.#showButton('menu');
    }

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        this.#showButton('menu');
        details.removeAttribute('data-animating');
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Toggle visibility of announcement bar
   * @param {boolean} hide - Whether to hide the announcement bar
   */
  #toggleAnnouncementBar(hide) {
    const headerGroup = document.querySelector('#header-group');
    const announcement = headerGroup?.querySelector('.announcement-bar');
    
    if (announcement && announcement instanceof HTMLElement) {
      if (hide) {
        announcement.style.display = 'none';
        announcement.setAttribute('data-drawer-hidden', 'true');
      } else {
        announcement.style.display = '';
        announcement.removeAttribute('data-drawer-hidden');
      }
    }
  }

  /**
   * Initialize button references
   */
  #initializeButtons() {
    this.menuButton = this.querySelector('[data-drawer-button="menu"]');
    this.closeButton = this.querySelector('[data-drawer-button="close"]');
    this.backButton = this.querySelector('[data-drawer-button="back"]');
    this.#showButton('menu');
  }

  /**
   * Show specific button and hide others
   * @param {'menu' | 'close' | 'back'} buttonType
   */
  #showButton(buttonType) {
    if (this.menuButton instanceof HTMLElement) {
      this.menuButton.style.display = buttonType === 'menu' ? '' : 'none';
    }
    if (this.closeButton instanceof HTMLElement) {
      this.closeButton.style.display = buttonType === 'close' ? '' : 'none';
    }
    if (this.backButton instanceof HTMLElement) {
      this.backButton.style.display = buttonType === 'back' ? '' : 'none';
    }
  }

  /**
   * Setup listeners for submenu open/close events
   */
  #setupSubmenuListeners() {
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const summary = target.closest('summary');
      if (!summary) return;

      const details = summary.parentElement;
      if (!(details instanceof HTMLDetailsElement)) return;
      
      if (details === this.refs.details) return;
      if (!details.classList.contains('menu-drawer__menu-container')) return;

      setTimeout(() => {
        if (details.hasAttribute('open')) {
          this.#showButton('back');
        } else {
          this.#showButton('close');
        }
      }, 0);
    });
  }

  #setupNavigationLinkListeners() {
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      if (this.isOpen) {
        this.close();
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Listen for the search modal opening and close the drawer if it's open.
   */
  #setupSearchModalListener() {
    const searchModal = document.querySelector('#search-modal');
    if (searchModal) {
      searchModal.addEventListener('dialog:open', () => {
        if (this.isOpen) {
          this.close();
        }
      });
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
